// Shared sprites for the meadow backgrounds (night and day).
// Each one is drawn once to an offscreen canvas, then reused every frame.

export const TAU = Math.PI * 2;
export const rand = (a: number, b: number) => a + Math.random() * (b - a);
export const canvasOf = (w: number, h: number) => {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
};

// Cheap blur that works in every browser: shrink, then let drawImage scale it back up.
export function soften(src: HTMLCanvasElement, factor: number): HTMLCanvasElement {
  const c = canvasOf(src.width / factor, src.height / factor);
  const x = c.getContext('2d')!;
  x.imageSmoothingQuality = 'high';
  x.drawImage(src, 0, 0, c.width, c.height);
  return c;
}

// Tint a sprite toward the sky colour so it sits back in the distance.
export function tint(c: HTMLCanvasElement, amount: number, rgb = '8, 30, 52') {
  if (amount <= 0) return;
  const x = c.getContext('2d')!;
  x.globalCompositeOperation = 'source-atop';
  x.fillStyle = `rgba(${rgb}, ${amount})`;
  x.fillRect(0, 0, c.width, c.height);
  x.globalCompositeOperation = 'source-over';
}

// A sunflower head with uneven petals, a shaded seed disk and a ring of florets.
export function makeHead(night: number, rgb?: string): HTMLCanvasElement {
  const R = 110;
  const c = canvasOf(R * 2.8, R * 2.8);
  const x = c.getContext('2d')!;
  x.translate(c.width / 2, c.height / 2);

  // Green bracts peeking out behind the petals.
  x.fillStyle = '#2f5a24';
  for (let i = 0; i < 18; i++) {
    x.save();
    x.rotate((i / 18) * TAU + 0.1);
    x.beginPath();
    x.moveTo(-R * 0.08, -R * 0.4);
    x.quadraticCurveTo(0, -R * 0.95, R * 0.08, -R * 0.4);
    x.fill();
    x.restore();
  }

  const petals = (n: number, len: number, wid: number, dark: number) => {
    for (let i = 0; i < n; i++) {
      const l = R * len * rand(0.86, 1.1);
      const w = R * wid * rand(0.8, 1.15);
      const bend = rand(-0.25, 0.25) * w;
      x.save();
      x.rotate((i / n) * TAU + rand(-0.06, 0.06));
      const g = x.createLinearGradient(0, -R * 0.36, 0, -l);
      g.addColorStop(0, dark ? '#9c5208' : '#c8720c');
      g.addColorStop(0.35, dark ? '#d98a16' : '#f0a51c');
      g.addColorStop(1, dark ? '#e9a93a' : '#ffd24f');
      x.fillStyle = g;
      x.beginPath();
      x.moveTo(-w * 0.35, -R * 0.36);
      x.bezierCurveTo(-w, -l * 0.55, -w * 0.5 + bend, -l * 0.92, bend * 0.6, -l);
      x.bezierCurveTo(w * 0.5 + bend, -l * 0.92, w, -l * 0.55, w * 0.35, -R * 0.36);
      x.closePath();
      x.fill();
      // Vein and a soft highlight along one edge.
      x.strokeStyle = 'rgba(150, 80, 10, 0.35)';
      x.lineWidth = 1.2;
      x.beginPath();
      x.moveTo(0, -R * 0.38);
      x.quadraticCurveTo(bend * 0.3, -l * 0.6, bend * 0.55, -l * 0.95);
      x.stroke();
      x.strokeStyle = 'rgba(255, 236, 170, 0.35)';
      x.beginPath();
      x.moveTo(-w * 0.3, -R * 0.45);
      x.quadraticCurveTo(-w * 0.75, -l * 0.6, bend * 0.3 - w * 0.2, -l * 0.9);
      x.stroke();
      x.restore();
    }
  };
  petals(30, 1.12, 0.17, 1);
  petals(27, 1.0, 0.16, 0);

  // Shadow the disk casts on the petals.
  const sh = x.createRadialGradient(0, 0, R * 0.4, 0, 0, R * 0.62);
  sh.addColorStop(0, 'rgba(60, 25, 0, 0.55)');
  sh.addColorStop(1, 'rgba(60, 25, 0, 0)');
  x.fillStyle = sh;
  x.beginPath();
  x.arc(0, 0, R * 0.62, 0, TAU);
  x.fill();

  // Disk: outer floret ring, then the seed spiral.
  const D = R * 0.44;
  const disk = x.createRadialGradient(-D * 0.2, -D * 0.25, 0, 0, 0, D);
  disk.addColorStop(0, '#3b220a');
  disk.addColorStop(0.7, '#2a1606');
  disk.addColorStop(0.86, '#6b4410');
  disk.addColorStop(1, '#8f6516');
  x.fillStyle = disk;
  x.beginPath();
  x.arc(0, 0, D, 0, TAU);
  x.fill();
  for (let i = 0; i < 520; i++) {
    const a = i * 2.39996;
    const d = Math.sqrt(i / 520) * D * 0.96;
    const k = d / D;
    x.fillStyle = k > 0.8
      ? `rgba(${200 + Math.random() * 40}, ${150 + Math.random() * 40}, 40, 0.8)`
      : `rgba(${70 + k * 60}, ${38 + k * 30}, 12, 0.9)`;
    x.beginPath();
    x.arc(Math.cos(a) * d, Math.sin(a) * d, 1.2 + k * 1.3, 0, TAU);
    x.fill();
  }
  // Dome light.
  const dome = x.createRadialGradient(-D * 0.35, -D * 0.4, 0, 0, 0, D);
  dome.addColorStop(0, 'rgba(255, 220, 150, 0.18)');
  dome.addColorStop(1, 'rgba(0, 0, 0, 0.25)');
  x.fillStyle = dome;
  x.beginPath();
  x.arc(0, 0, D, 0, TAU);
  x.fill();

  x.setTransform(1, 0, 0, 1, 0, 0);
  tint(c, night, rgb);
  return c;
}

// A broad heart-shaped leaf with veins. Origin is the left middle (the stalk).
export function makeLeaf(night: number, rgb?: string): HTMLCanvasElement {
  const c = canvasOf(140, 100);
  const x = c.getContext('2d')!;
  const g = x.createLinearGradient(0, 20, 0, 90);
  g.addColorStop(0, '#4a7f33');
  g.addColorStop(1, '#23461d');
  x.fillStyle = g;
  x.beginPath();
  x.moveTo(20, 50);
  x.bezierCurveTo(30, 6, 100, 2, 138, 46);
  x.bezierCurveTo(104, 92, 34, 96, 20, 50);
  x.fill();
  x.strokeStyle = '#2f5a24';
  x.lineWidth = 3;
  x.beginPath();
  x.moveTo(0, 50);
  x.lineTo(20, 50);
  x.stroke();
  x.strokeStyle = 'rgba(160, 200, 120, 0.45)';
  x.lineWidth = 1.4;
  x.beginPath();
  x.moveTo(20, 50);
  x.quadraticCurveTo(80, 40, 134, 46);
  for (let i = 1; i < 5; i++) {
    const px = 20 + i * 24;
    x.moveTo(px, 47 - i);
    x.quadraticCurveTo(px + 14, 30, px + 22, 22 + i * 2);
    x.moveTo(px, 49 - i);
    x.quadraticCurveTo(px + 14, 66, px + 20, 76 - i * 2);
  }
  x.stroke();
  tint(c, night, rgb);
  return c;
}

// Small golden and orange wildflowers, like the ones glowing in the old video.
export function makeBloom(col: string, petals: number): HTMLCanvasElement {
  const c = canvasOf(64, 64);
  const x = c.getContext('2d')!;
  x.translate(32, 32);
  const glow = x.createRadialGradient(0, 0, 0, 0, 0, 32);
  glow.addColorStop(0, 'rgba(255, 190, 90, 0.35)');
  glow.addColorStop(1, 'rgba(255, 190, 90, 0)');
  x.fillStyle = glow;
  x.fillRect(-32, -32, 64, 64);
  x.fillStyle = col;
  for (let i = 0; i < petals; i++) {
    x.save();
    x.rotate((i / petals) * TAU);
    x.beginPath();
    x.ellipse(0, -9, 4.2, 9, 0, 0, TAU);
    x.fill();
    x.restore();
  }
  x.fillStyle = '#6a3a0a';
  x.beginPath();
  x.arc(0, 0, 3.6, 0, TAU);
  x.fill();
  return c;
}

export function makeGlow(rgb: string, core = 0.45): HTMLCanvasElement {
  const c = canvasOf(128, 128);
  const x = c.getContext('2d')!;
  const g = x.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, `rgba(${rgb}, 1)`);
  g.addColorStop(0.25, `rgba(${rgb}, ${core})`);
  g.addColorStop(1, `rgba(${rgb}, 0)`);
  x.fillStyle = g;
  x.fillRect(0, 0, 128, 128);
  return c;
}

// Soft film grain, made once.
export const GRAIN = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>")`;

// A white daisy seen from slightly above.
export function makeDaisy(): HTMLCanvasElement {
  const c = canvasOf(64, 64);
  const x = c.getContext('2d')!;
  x.translate(32, 32);
  const n = 16;
  for (let i = 0; i < n; i++) {
    x.save();
    x.rotate((i / n) * TAU + rand(-0.05, 0.05));
    const g = x.createLinearGradient(0, -4, 0, -27);
    g.addColorStop(0, '#e8e6dc');
    g.addColorStop(1, '#ffffff');
    x.fillStyle = g;
    x.beginPath();
    x.ellipse(0, -16, 3.4, rand(10, 12.5), 0, 0, TAU);
    x.fill();
    x.restore();
  }
  const d = x.createRadialGradient(-2, -2, 0, 0, 0, 7);
  d.addColorStop(0, '#ffe066');
  d.addColorStop(1, '#e3a412');
  x.fillStyle = d;
  x.beginPath();
  x.arc(0, 0, 7, 0, TAU);
  x.fill();
  return c;
}

// An orange poppy: a few broad, cupped petals and a dark heart.
export function makePoppy(): HTMLCanvasElement {
  const c = canvasOf(64, 64);
  const x = c.getContext('2d')!;
  x.translate(32, 32);
  for (let i = 0; i < 5; i++) {
    x.save();
    x.rotate((i / 5) * TAU + rand(-0.15, 0.15));
    const g = x.createRadialGradient(0, -6, 2, 0, -12, 18);
    g.addColorStop(0, '#f06a1d');
    g.addColorStop(1, '#ff9d3b');
    x.fillStyle = g;
    x.beginPath();
    x.moveTo(0, 0);
    x.bezierCurveTo(-17, -8, -15, -27, 0, -26);
    x.bezierCurveTo(15, -27, 17, -8, 0, 0);
    x.fill();
    x.restore();
  }
  x.fillStyle = '#3a2410';
  x.beginPath();
  x.arc(0, 0, 4.5, 0, TAU);
  x.fill();
  return c;
}

// A soft, fluffy cloud: a flat-ish base and a bright, puffy top.
export function makeCloud(): HTMLCanvasElement {
  const c = canvasOf(420, 180);
  const x = c.getContext('2d')!;
  const puffs = 18;
  for (let i = 0; i < puffs; i++) {
    const u = i / (puffs - 1);
    const px = 60 + u * 300 + rand(-14, 14);
    const lift = Math.sin(u * Math.PI);
    const r = rand(26, 40) + lift * rand(16, 30);
    const py = 128 - r * 0.55 - lift * rand(10, 30);
    const g = x.createRadialGradient(px - r * 0.25, py - r * 0.35, r * 0.15, px, py, r);
    g.addColorStop(0, 'rgba(255, 255, 255, 1)');
    g.addColorStop(0.75, 'rgba(252, 253, 255, 0.92)');
    g.addColorStop(1, 'rgba(240, 245, 252, 0)');
    x.fillStyle = g;
    x.beginPath();
    x.arc(px, py, r, 0, TAU);
    x.fill();
  }
  // A faint cool shade along the underside.
  x.globalCompositeOperation = 'source-atop';
  const s = x.createLinearGradient(0, 70, 0, 140);
  s.addColorStop(0, 'rgba(200, 212, 230, 0)');
  s.addColorStop(1, 'rgba(205, 216, 232, 0.28)');
  x.fillStyle = s;
  x.fillRect(0, 0, 420, 180);
  return soften(c, 1.4);
}

// Butterfly species, painted as a right-hand pair of wings (fore and hind).
export type Species = {
  base: [string, string]; vein: string; veinW: number;
  border?: string; borderW?: number; dots?: boolean; tip?: string; spot?: string;
};
export const SPECIES: Record<string, Species> = {
  monarch: { base: ['#c9540f', '#f5a03a'], vein: '#1b120b', veinW: 2.4, border: '#17100a', borderW: 10, dots: true },
  blue: { base: ['#355fc4', '#9cc0f5'], vein: 'rgba(25, 35, 80, 0.35)', veinW: 0.9, border: '#2b3048', borderW: 3 },
  white: { base: ['#e9e6d8', '#fffdf5'], vein: 'rgba(110, 110, 100, 0.3)', veinW: 0.8, tip: '#2b2b2b', spot: '#2b2b2b' },
  lemon: { base: ['#e2cf4a', '#f8f0a2'], vein: 'rgba(120, 110, 40, 0.3)', veinW: 0.8, spot: '#e88a2a' },
  zuzu: { base: ['#e79a1c', '#ffd66b'], vein: 'rgba(120, 60, 10, 0.6)', veinW: 1.4, border: '#6b3a0c', borderW: 4, dots: true },
};

// Wing root sits at (6, 60) in a 120x120 canvas. The forewing points up (toward
// the head), the hindwing down, and both reach to the right.
export function makeWings(sp: Species): HTMLCanvasElement {
  const c = canvasOf(120, 120);
  const x = c.getContext('2d')!;
  const fore = new Path2D('M6 57 C30 18 66 4 97 9 C106 11 108 20 104 26 C100 44 88 58 70 64 C48 68 24 66 6 62 Z');
  const hind = new Path2D('M6 62 C30 60 58 60 78 70 C90 80 84 100 66 108 C48 116 30 110 20 96 C12 86 8 74 6 66 Z');

  const paint = (path: Path2D, veins: [number, number][], spotAt: [number, number]) => {
    x.save();
    x.clip(path);
    const g = x.createRadialGradient(6, 60, 4, 6, 60, 105);
    g.addColorStop(0, sp.base[0]);
    g.addColorStop(1, sp.base[1]);
    x.fillStyle = g;
    x.fillRect(0, 0, 120, 120);
    x.strokeStyle = sp.vein;
    x.lineWidth = sp.veinW;
    x.lineCap = 'round';
    for (const [vx, vy] of veins) {
      x.beginPath();
      x.moveTo(6, 60);
      x.quadraticCurveTo((6 + vx) / 2, (60 + vy) / 2 + (vy < 60 ? 6 : -6), vx, vy);
      x.stroke();
    }
    if (sp.border) {
      x.strokeStyle = sp.border;
      x.lineWidth = sp.borderW ?? 4;
      x.stroke(path);
    }
    if (sp.tip && path === fore) {
      x.fillStyle = sp.tip;
      x.beginPath();
      x.arc(104, 12, 22, 0, TAU);
      x.fill();
    }
    if (sp.spot) {
      x.fillStyle = sp.spot;
      x.beginPath();
      x.arc(spotAt[0], spotAt[1], 3.4, 0, TAU);
      x.fill();
    }
    x.restore();
  };
  paint(hind, [[80, 74], [76, 92], [62, 108], [42, 112], [24, 100]], [48, 88]);
  paint(fore, [[98, 10], [104, 24], [96, 42], [84, 56], [66, 64], [40, 66]], [66, 34]);

  // White dots along the dark border (monarch style).
  if (sp.dots) {
    x.fillStyle = 'rgba(255, 255, 255, 0.9)';
    const dot = (px: number, py: number, r: number) => { x.beginPath(); x.arc(px, py, r, 0, TAU); x.fill(); };
    [[99, 16], [103, 24], [98, 38], [90, 50], [79, 59], [92, 12], [84, 8]].forEach(([px, py]) => dot(px, py, 1.5));
    [[80, 80], [76, 92], [68, 102], [56, 108], [42, 108]].forEach(([px, py]) => dot(px, py, 1.4));
  }
  return c;
}

// Smooth 2D value noise with fractal layers (fBm), for clouds and star dust.
export function makeNoise2D() {
  const perm = new Uint8Array(512);
  const vals = new Float32Array(256);
  for (let i = 0; i < 256; i++) { perm[i] = i; vals[i] = Math.random(); }
  for (let i = 255; i > 0; i--) { const j = Math.random() * (i + 1) | 0; [perm[i], perm[j]] = [perm[j], perm[i]]; }
  for (let i = 0; i < 256; i++) perm[i + 256] = perm[i];
  const v = (x: number, y: number) => vals[perm[(perm[x & 255] + y) & 255]];
  const noise = (x: number, y: number) => {
    const ix = Math.floor(x), iy = Math.floor(y), fx = x - ix, fy = y - iy;
    const u = fx * fx * (3 - 2 * fx), w = fy * fy * (3 - 2 * fy);
    const a = v(ix, iy), b = v(ix + 1, iy), c = v(ix, iy + 1), d = v(ix + 1, iy + 1);
    return a + (b - a) * u + (c - a) * w + (a - b - c + d) * u * w;
  };
  return (x: number, y: number, octaves = 5) => {
    let s = 0, amp = 0.5, fr = 1, norm = 0;
    for (let o = 0; o < octaves; o++) {
      s += noise(x * fr + o * 31.7, y * fr - o * 17.3) * amp;
      norm += amp; amp *= 0.5; fr *= 2.03;
    }
    return s / norm;
  };
}
